import { useState } from 'react'
import type { ContentVersionAnnotation } from '@opallabs/api'
import { Button, Icon } from '@opallabs/lapidary'
import { MentionText } from './mentionText'
import * as styles from './styles'

const formatTimestamp = (isoDate: string): string =>
  new Date(isoDate).toLocaleString([], {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })

type FeedbackCardProps = {
  /** Open feedback, in pin order. */
  annotations: ContentVersionAnnotation[]
  /** Resolved feedback, which has no pin on the canvas to anchor a popover to. */
  resolvedAnnotations: ContentVersionAnnotation[]
  /**
   * Resolved feedback a deep link points at: its row is marked and scrolls
   * itself into view, since there is no pin on the canvas to open instead.
   */
  highlightedId?: string | null
  hoveredId: string | null
  onHoverChange: (id: string | null) => void
  onSelect: (id: string) => void
  onUnresolve: (id: string) => void
}

/**
 * Always-visible summary list of a version's pin annotations, sharing hover state
 * with the pin overlay so hovering a row rings its pin and vice versa. Clicking a
 * row opens that pin's comment popover on the preview.
 *
 * Resolved feedback has left the canvas, so it gathers into a group at the
 * bottom: rows only, each with the affordance to reopen it.
 */
export const FeedbackCard: React.VFC<FeedbackCardProps> = ({
  annotations,
  resolvedAnnotations,
  highlightedId = null,
  hoveredId,
  onHoverChange,
  onSelect,
  onUnresolve,
}) => (
  <>
    <h3 css={styles.sectionHeading}>Feedback</h3>
    <section css={styles.feedbackCard}>
      {annotations.length === 0 && resolvedAnnotations.length === 0 ? (
        <p css={styles.feedbackEmpty}>No feedback yet.</p>
      ) : (
        <>
          {annotations.length > 0 && (
            <ul css={styles.feedbackList}>
              {annotations.map((annotation, index) => (
                <li key={annotation.id}>
                  <button
                    type="button"
                    css={styles.feedbackRow(hoveredId === annotation.id)}
                    onClick={() => onSelect(annotation.id)}
                    onMouseEnter={() => onHoverChange(annotation.id)}
                    onMouseLeave={() => onHoverChange(null)}
                  >
                    <span css={styles.feedbackBadge}>{index + 1}</span>
                    <div css={styles.feedbackRowBody}>
                      <p css={styles.feedbackComment}>
                        <MentionText text={annotation.comment} />
                      </p>
                      <span css={styles.feedbackMeta}>
                        {annotation.authorName} ·{' '}
                        {formatTimestamp(annotation.createdAt)}
                      </span>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {resolvedAnnotations.length > 0 && (
            <ResolvedGroup
              annotations={resolvedAnnotations}
              divided={annotations.length > 0}
              highlightedId={highlightedId}
              onUnresolve={onUnresolve}
            />
          )}
        </>
      )}
    </section>
  </>
)

type ResolvedGroupProps = {
  annotations: ContentVersionAnnotation[]
  divided: boolean
  highlightedId: string | null
  onUnresolve: (id: string) => void
}

/** Brings a deep-linked row into view; stable so the ref fires once per row. */
const scrollRowIntoView = (node: HTMLLIElement | null) =>
  node?.scrollIntoView({ behavior: 'smooth', block: 'center' })

const ResolvedGroup: React.VFC<ResolvedGroupProps> = ({
  annotations,
  divided,
  highlightedId,
  onUnresolve,
}) => {
  // Open by default: the group only renders when it holds something, and a
  // reader who cannot see the rows reads "Resolved (2)" as feedback with no way
  // back — the Unresolve action is inside.
  const [expanded, setExpanded] = useState(true)

  return (
    <div css={styles.resolvedGroup(divided)}>
      <button
        type="button"
        aria-expanded={expanded}
        css={styles.resolvedToggle}
        onClick={() => setExpanded(!expanded)}
      >
        <Icon ariaHidden glyph={expanded ? 'gem-chevron-s' : 'gem-chevron-e'} />
        {`Resolved (${annotations.length})`}
      </button>
      {expanded && (
        <ul css={styles.feedbackList}>
          {annotations.map((annotation) => (
            <li
              key={annotation.id}
              css={styles.resolvedRow(annotation.id === highlightedId)}
              ref={
                annotation.id === highlightedId ? scrollRowIntoView : undefined
              }
            >
              <div css={styles.feedbackRowBody}>
                <p css={styles.feedbackComment}>
                  <MentionText text={annotation.comment} />
                </p>
                <span css={styles.feedbackMeta}>
                  {annotation.authorName} ·{' '}
                  {formatTimestamp(annotation.createdAt)}
                </span>
              </div>
              <Button
                buttonText="Unresolve"
                variant="tertiary"
                name="Unresolve feedback"
                onClick={() => onUnresolve(annotation.id)}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
